import * as React from "react";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/convexApi";

export interface QuickCreatedVendor {
  _id: string;
  displayName: string;
  qboPayeeName: string;
  active: true;
}

export function QuickCreateVendorDialog({
  open,
  initialName,
  authUserId,
  onOpenChange,
  onCreated,
  onRestoreFocus,
}: {
  open: boolean;
  initialName: string;
  authUserId: string | undefined;
  onOpenChange: (open: boolean) => void;
  onCreated: (vendor: QuickCreatedVendor) => void;
  onRestoreFocus: () => void;
}) {
  const createVendor = useMutation(api.accountingBillMasters.createVendor);
  const [displayName, setDisplayName] = React.useState("");
  const [qboPayeeName, setQboPayeeName] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const normalizedName = initialName.trim().replace(/\s+/g, " ");
    setDisplayName(normalizedName);
    setQboPayeeName(normalizedName);
  }, [initialName, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSaving && !nextOpen) return;
    onOpenChange(nextOpen);
  };

  const handleCreate = async () => {
    if (!authUserId) {
      toast.error("You are not signed in — reload the page and try again");
      return;
    }

    const nextDisplayName = displayName.trim();
    const nextQboPayeeName = qboPayeeName.trim();
    if (!nextDisplayName || !nextQboPayeeName) return;

    setIsSaving(true);
    try {
      const vendorId = await createVendor({
        authUserId,
        displayName: nextDisplayName,
        qboPayeeName: nextQboPayeeName,
      });
      if (typeof vendorId !== "string" || !vendorId) {
        throw new Error(
          "The vendor was created but could not be selected. Refresh and choose it from the list.",
        );
      }
      onCreated({
        _id: vendorId,
        displayName: nextDisplayName,
        qboPayeeName: nextQboPayeeName,
        active: true,
      });
      toast.success("Vendor created and selected");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not create vendor",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onRestoreFocus();
        }}
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleCreate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Quick add vendor</DialogTitle>
            <DialogDescription>
              Confirm how this payee appears in QuickBooks. You can add
              addresses, terms, aliases, and defaults later in Bills settings.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="quick-vendor-display-name">Display name</Label>
              <Input
                id="quick-vendor-display-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="quick-vendor-qbo-payee-name">
                QBO payee name
              </Label>
              <Input
                id="quick-vendor-qbo-payee-name"
                value={qboPayeeName}
                onChange={(event) => setQboPayeeName(event.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                This must exactly match the payee name in QuickBooks.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isSaving}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSaving || !displayName.trim() || !qboPayeeName.trim()
              }
            >
              {isSaving && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isSaving ? "Creating…" : "Create and select"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
